import { PropsWithChildren, CSSProperties } from 'react';
import { hasMedia } from './Media.utils';
import { sanitize } from '../../utils/sanitize';

export interface MediaProps extends PropsWithChildren {
  /** Aspect ratio of media */
  aspect?: 'none' | 'landscape' | 'portrait' | 'square' | 'wide';
  /** Source of media */
  src?: string;
  /** Alternative text of media */
  alt?: string;
  /** Title of media */
  title?: string;
  /** Caption of media */
  caption?: string;
  /** Additional class name of media */
  className?: string;
  /** Render media at intrinsic size unless the container is smaller */
  originalSize?: boolean;
  /** Embed the source */
  embed?: boolean;
  /** Contain media in container rather than cover the whole space */
  contain?: boolean;
  /** The focus point */
  focus_point?:
    | 'default'
    | 'top_left'
    | 'top_center'
    | 'top_right'
    | 'center_left'
    | 'center_center'
    | 'center_right'
    | 'bottom_left'
    | 'bottom_center'
    | 'bottom_right';
  poster?: string;
  /** Styles for figure element */
  style?: CSSProperties;
}

/** A media object (image or video) */
export const Media = ({
  aspect = 'none',
  src,
  alt,
  title,
  caption,
  className,
  embed,
  contain,
  children,
  focus_point = 'default',
  style,
}: MediaProps) => {
  if (!hasMedia({ src, embed, children })) {
    return null;
  }
  return (
    <figure
      className={[
        'media',
        `media--${aspect}`,
        `media--focus-${focus_point}`,
        contain ? 'media--contain' : '',
        className,
      ].join(' ')}
      style={style}
    >
      {src && !embed && (
        <img className="media__object" src={src} alt={alt} title={title} />
      )}
      {src && embed && (
        <div
          className="media__object media__object--embed"
          dangerouslySetInnerHTML={{ __html: sanitize(src) }}
        />
      )}
      {children}
      {(caption || title) && (
        <figcaption className="media__caption">
          {title && <strong>{title}</strong>}
          {caption && title && <br />}
          {caption}
        </figcaption>
      )}
    </figure>
  );
};
